/**
 * Overview page — recent analytics plus current workspace snapshots.
 *
 * Snapshot sections like subscriptions stay stable, while the page-level
 * window toggle controls the recent analytics cards and supporting panels.
 */

import { useEffect, useState } from 'react';
import { useWorkspace } from '../hooks/useWorkspace';
import * as api from '../api-client';
import {
  AdminMetricTile,
  AdminPage,
  AdminPageHeader,
  AdminPanel,
  AdminStatCard,
} from '../components/AdminPrimitives';

const CATEGORY_ORDER = [
  'Product Discovery',
  'Product Details & Compatibility',
  'Pricing & Promotions',
  'Shipping & Returns',
  'Order Status & Support',
  'Recommendations & Gifting',
  'Sleep Science / FAQ',
  'Store Policy / FAQ',
];

const LENGTH_BUCKET_ORDER = ['1', '2-3', '4-7', '8+'];
const LENGTH_BUCKET_LABELS: Record<string, string> = {
  '1': '1 Turn',
  '2-3': '2-3 Turns',
  '4-7': '4-7 Turns',
  '8+': '8+ Turns',
};

const LINK_PAGE_TYPE_LABELS: Record<string, string> = {
  product_page: 'Product Page',
  collection_page: 'Collection Page',
  info_page: 'Info Page',
  other: 'Other',
};

type DimensionTotal = {
  dimension: string;
  value: number;
};

type ComparisonCard = {
  label: string;
  value: string;
  delta: string;
  trend: 'up' | 'down' | 'flat';
  sub: string;
};

type WindowPreset = 30 | 7;

export function OverviewPage() {
  const { workspace } = useWorkspace();
  const [metrics, setMetrics] = useState<api.MetricPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedWindowDays, setSelectedWindowDays] = useState<WindowPreset>(30);

  useEffect(() => {
    if (!workspace) return;
    let cancelled = false;
    const workspaceKey = workspace.workspace_key;

    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        const res = await api.getOverview(workspaceKey);
        if (!cancelled) {
          setMetrics(res.metrics);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof api.AdminApiError ? err.detail : 'Failed to load overview');
          setIsLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [workspace]);

  if (!workspace) return null;

  const recentWindow = getCompletedWindow(metrics, selectedWindowDays);
  const recentMetrics = recentWindow ? filterMetricsToWindow(metrics, recentWindow) : metrics;
  const recentTotals = new Map<string, number>();
  for (const metric of recentMetrics) {
    if (metric.dimension === null) {
      recentTotals.set(metric.metric_name, (recentTotals.get(metric.metric_name) ?? 0) + metric.value);
    }
  }

  const totalConversations = recentTotals.get('conversation.count') ?? 0;
  const linkedConversations = recentTotals.get('conversation.linked_count') ?? 0;
  const assistedSessions = recentTotals.get('conversation.assisted_count') ?? linkedConversations;
  const totalLinks = recentTotals.get('conversation.total_links') ?? 0;

  const hasSubscriptionMetrics = metrics.some((m) => m.metric_name.startsWith('subscription.'));
  const subscriptionTotal = getLatestMetricValue(metrics, 'subscription.total_active');
  const subscriptionFree = getLatestMetricValue(metrics, 'subscription.free_active');
  const subscriptionPaid = getLatestMetricValue(metrics, 'subscription.paid_active');
  const subscriptionPlans = getLatestDimensionSnapshot(metrics, 'subscription.by_plan');
  const paidShare = subscriptionTotal > 0 ? subscriptionPaid / subscriptionTotal : 0;

  const hasThinkWithMeMetrics = metrics.some((m) => m.metric_name.startsWith('think_with_me.'));
  const isCommerceWorkspace = workspace.admin_experience_type === 'ecommerce';
  const twmActivatedUsers = getLatestMetricValue(metrics, 'think_with_me.activated_users');
  const twmRecentUsers = getLatestMetricValue(metrics, 'think_with_me.active_users_30d');
  const twmRecentUsers7d = getLatestMetricValue(metrics, 'think_with_me.active_users_7d');
  const twmActivationRate = getLatestMetricValue(metrics, 'think_with_me.activation_rate');
  const twmTotalActivations = getLatestMetricValue(metrics, 'think_with_me.total_activations');
  const twmAvgActivations = getLatestMetricValue(metrics, 'think_with_me.avg_activations_per_user');
  const twmWindowUsers =
    selectedWindowDays === 7 ? twmRecentUsers7d || twmRecentUsers : twmRecentUsers;
  const twmUsageShare = twmActivatedUsers > 0 ? twmWindowUsers / twmActivatedUsers : 0;

  const hasGuestModeMetrics = recentMetrics.some((m) => m.metric_name.startsWith('guest_mode.'));
  const guestTrialTurns = recentTotals.get('guest_mode.trial_turns') ?? 0;
  const guestLimitReached = recentTotals.get('guest_mode.limit_reached') ?? 0;
  const guestUpgrades = recentTotals.get('guest_mode.upgrades') ?? 0;
  const guestLimitToUpgradeRate = guestLimitReached > 0 ? guestUpgrades / guestLimitReached : 0;

  const categoryBreakdown = sortByValueWithPreferredOrder(
    sumDimensionMetrics(recentMetrics, 'conversation.by_category'),
    CATEGORY_ORDER,
  )
    .filter((item) => item.value > 0)
    .slice(0, 6);
  const lengthBreakdown = orderByPreferredOrder(
    sumDimensionMetrics(recentMetrics, 'conversation.by_length_bucket'),
    LENGTH_BUCKET_ORDER,
  )
    .filter((item) => item.value > 0);
  const linkedPageTypes = sumDimensionMetrics(recentMetrics, 'conversation.by_linked_page_type')
    .filter((item) => item.value > 0)
    .slice(0, 4);

  const hasCategoryData = isCommerceWorkspace && categoryBreakdown.length > 0;
  const hasLengthData = lengthBreakdown.length > 0;
  const hasLinkData = isCommerceWorkspace && (linkedConversations > 0 || linkedPageTypes.length > 0);
  const linkShare = totalConversations > 0 ? linkedConversations / totalConversations : 0;
  const avgLinksPerLinkedChat = linkedConversations > 0 ? totalLinks / linkedConversations : 0;
  const avgTurnsWindow = computeAvg(recentMetrics, 'conversation.avg_turns');
  const exportReady = !isLoading && metrics.length > 0;
  const windowLabel = selectedWindowDays === 30 ? 'Last 30 Days' : 'Last 7 Days';
  const windowThroughLabel = recentWindow ? `Through ${formatShortDate(recentWindow.end)}` : windowLabel;
  const windowPhrase = selectedWindowDays === 30 ? 'last 30 complete days' : 'last 7 complete days';

  const cards: Array<{ label: string; value: string; sub: string; tone?: 'default' | 'success' | 'warning' | 'danger' }> = isCommerceWorkspace
    ? [
        {
          label: 'Conversations',
          value: formatNumber(totalConversations),
          sub: windowThroughLabel,
        },
        {
          label: 'Avg Daily Active Users',
          value: formatNumber(computeAvg(recentMetrics, 'conversation.active_users')),
          sub: `Average per day in ${windowPhrase}`,
        },
        {
          label: 'Avg Turns / Conversation',
          value: formatDecimal(avgTurnsWindow),
          sub: `Average in ${windowPhrase}`,
        },
        {
          label: 'Assisted Sessions',
          value: formatNumber(assistedSessions),
          sub: `In ${windowPhrase}`,
          tone: 'success',
        },
      ]
    : [
        {
          label: 'Conversations',
          value: formatNumber(totalConversations),
          sub: windowThroughLabel,
        },
        {
          label: 'Avg Daily Active Users',
          value: formatNumber(computeAvg(recentMetrics, 'conversation.active_users')),
          sub: `Average per day in ${windowPhrase}`,
        },
        {
          label: 'Avg Turns / Conversation',
          value: formatDecimal(avgTurnsWindow),
          sub: `Average in ${windowPhrase}`,
        },
        hasThinkWithMeMetrics
          ? {
              label: 'Think With Me Active Users',
              value: formatNumber(twmWindowUsers),
              sub: windowThroughLabel,
              tone: 'success',
            }
          : {
              label: 'Paid Subscribers',
              value: formatNumber(subscriptionPaid),
              sub: 'Latest subscriber snapshot',
            },
      ];

  const comparisonCards = buildWeeklyComparisonCards(metrics, {
    experienceType: workspace.admin_experience_type,
    paidSubscribers: subscriptionPaid,
  });

  return (
    <AdminPage>
      <AdminPageHeader
        title="Overview"
        subtitle="Review recent engagement, assistance quality, and current workspace health."
        actions={
          exportReady ? (
            <button
              type="button"
              onClick={() => downloadMetricsCsv(workspace.display_name, metrics)}
              className="rounded-xl border border-[var(--border-default)] bg-white px-4 py-2 text-sm font-medium text-[var(--text-primary)] shadow-sm transition-colors hover:bg-[var(--interactive-hover)]"
            >
              Export CSV
            </button>
          ) : undefined
        }
      />

      {error && (
        <div
          role="alert"
          className="mb-4 p-3 text-sm rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-[var(--status-error)]"
        >
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-36 rounded-[1.4rem] bg-[var(--surface-tertiary)] animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {hasSubscriptionMetrics && (
            <section className="mb-6">
              <div className="mb-3 flex items-end justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text-primary)]">Subscriptions</h2>
                  <p className="mt-1 text-sm text-[var(--text-secondary)]">
                    Current subscriber mix for this workspace.
                  </p>
                </div>
                <span className="text-xs text-[var(--text-tertiary)]">Latest snapshot</span>
              </div>
              <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <AdminStatCard
                  label="Total Subscribers"
                  value={formatNumber(subscriptionTotal)}
                  sub="Current active access"
                />
                <AdminStatCard
                  label="Paid Subscribers"
                  value={formatNumber(subscriptionPaid)}
                  sub={subscriptionTotal > 0 ? `${formatPercent(paidShare)} of total` : 'Current paid access'}
                  tone="success"
                />
                <AdminStatCard
                  label="Free Subscribers"
                  value={formatNumber(subscriptionFree)}
                  sub="Default free tier"
                />
                <AdminStatCard
                  label="Paid Conversion"
                  value={formatPercent(paidShare)}
                  sub="Share of active subscribers"
                  tone="warning"
                />
              </div>
              {subscriptionPlans.length > 0 && (
                <AdminPanel
                  title="Subscription Tier Breakdown"
                  subtitle="Current active subscriber mix by plan"
                  rightSlot={<span>Latest snapshot</span>}
                >
                  <BarList
                    items={subscriptionPlans}
                    total={subscriptionTotal}
                    labelFormatter={(label) => label}
                    valueFormatter={(value, share) => `${formatNumber(value)} (${formatPercent(share)})`}
                  />
                </AdminPanel>
              )}
            </section>
          )}

          <section className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">Completed-Day Window</h2>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">
                Controls the completed-day Guest Mode and Recent Analytics sections below.
              </p>
            </div>
            <div
              className="inline-flex w-fit rounded-full border border-[var(--border-default)] bg-white p-1 shadow-sm"
              role="group"
              aria-label="Recent analytics window"
            >
              {[30, 7].map((days) => {
                const isActive = selectedWindowDays === days;
                return (
                  <button
                    key={days}
                    type="button"
                    onClick={() => setSelectedWindowDays(days as WindowPreset)}
                    className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-[var(--accent-primary)] text-white'
                        : 'text-[var(--text-secondary)] hover:bg-[var(--interactive-hover)]'
                    }`}
                  >
                    {days === 30 ? 'Last 30 Days' : 'Last 7 Days'}
                  </button>
                );
              })}
            </div>
          </section>

          {hasGuestModeMetrics && (
            <section className="mb-6">
              <div className="mb-3 flex items-end justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text-primary)]">Guest Mode</h2>
                  <p className="mt-1 text-sm text-[var(--text-secondary)]">
                    Anonymous trial usage and sign-in conversion across the selected completed-day window.
                  </p>
                </div>
                <span className="text-xs text-[var(--text-tertiary)]">{windowThroughLabel}</span>
              </div>
              <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <AdminStatCard
                  label="Trial Turns"
                  value={formatNumber(guestTrialTurns)}
                  sub={`Anonymous turns in ${windowPhrase}`}
                />
                <AdminStatCard
                  label="Limit Reached"
                  value={formatNumber(guestLimitReached)}
                  sub="Guest limit conversion moments"
                  tone={guestLimitReached > 0 ? 'warning' : 'default'}
                />
                <AdminStatCard
                  label="Sign-ins"
                  value={formatNumber(guestUpgrades)}
                  sub="Successful OTP or Google upgrades"
                  tone={guestUpgrades > 0 ? 'success' : 'default'}
                />
                <AdminStatCard
                  label="Limit-to-Sign-in Rate"
                  value={formatPercent(guestLimitToUpgradeRate)}
                  sub="Sign-ins after limit pressure"
                  tone={guestLimitToUpgradeRate > 0 ? 'success' : 'default'}
                />
              </div>
            </section>
          )}

          <section className="mb-4">
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">Recent Analytics</h2>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Windowed engagement and assistance metrics for this workspace.
            </p>
          </section>

          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {cards.map((card) => (
              <AdminStatCard
                key={card.label}
                label={card.label}
                value={card.value}
                sub={card.sub}
                tone={card.tone}
              />
            ))}
          </div>

          {comparisonCards.length > 0 && (
            <AdminPanel
              title="This Week vs Last Week"
              subtitle={
                isCommerceWorkspace
                  ? 'Compare recent shopper demand and knowledge coverage across complete 7-day windows.'
                  : 'Compare recent engagement, subscriber mix, and companion depth across complete 7-day windows.'
              }
              className="mb-6"
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {comparisonCards.map((card) => (
                  <ComparisonTile key={card.label} {...card} />
                ))}
              </div>
            </AdminPanel>
          )}

          {isCommerceWorkspace && (hasCategoryData || hasLengthData) && (
            <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
              {hasCategoryData && (
                <AdminPanel
                  title="Top Question Categories"
                  subtitle={`Share of conversations across the ${windowPhrase}`}
                >
                  <BarList
                    items={categoryBreakdown}
                    total={totalConversations}
                    labelFormatter={(label) => label}
                    valueFormatter={(value, share) => `${formatPercent(share)} • ${formatNumber(value)}`}
                  />
                </AdminPanel>
              )}

              {hasLengthData && (
                <AdminPanel
                  title="Conversation Length"
                  subtitle={`Distribution of conversation depth across the ${windowPhrase}`}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {lengthBreakdown.map((bucket) => {
                      const share = totalConversations > 0 ? bucket.value / totalConversations : 0;
                      return (
                        <AdminMetricTile
                          key={bucket.dimension}
                          label={LENGTH_BUCKET_LABELS[bucket.dimension] ?? bucket.dimension}
                          value={formatPercent(share)}
                          sub={`${formatNumber(bucket.value)} conversations`}
                        />
                      );
                    })}
                  </div>
                </AdminPanel>
              )}
            </div>
          )}

          {isCommerceWorkspace && hasLinkData && (
            <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
              {hasLinkData && (
                <AdminPanel
                  title="Link Assistance"
                  subtitle={`How often the assistant successfully guides navigation across the ${windowPhrase}`}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <AdminMetricTile
                      label="Links in Conversations"
                      value={formatPercent(linkShare)}
                      sub={`${formatNumber(linkedConversations)} linked chats`}
                    />
                    <AdminMetricTile
                      label="Avg. Links Per Linked Chat"
                      value={formatDecimal(avgLinksPerLinkedChat)}
                      sub={`${formatNumber(totalLinks)} links total`}
                    />
                  </div>
                  {linkedPageTypes.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-[var(--text-primary)] mb-3">
                        Top Linked Pages
                      </h4>
                      <BarList
                        items={linkedPageTypes}
                        total={totalLinks}
                        labelFormatter={(label) => LINK_PAGE_TYPE_LABELS[label] ?? label}
                        valueFormatter={(value, share) => `${formatNumber(value)} (${formatPercent(share)})`}
                      />
                    </div>
                  )}
                </AdminPanel>
              )}
            </div>
          )}

          {!isCommerceWorkspace && (hasLengthData || hasThinkWithMeMetrics) && (
            <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
              {hasLengthData && (
                <AdminPanel
                  title="Conversation Length"
                  subtitle={`Distribution of conversation depth across the ${windowPhrase}`}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {lengthBreakdown.map((bucket) => {
                      const share = totalConversations > 0 ? bucket.value / totalConversations : 0;
                      return (
                        <AdminMetricTile
                          key={bucket.dimension}
                          label={LENGTH_BUCKET_LABELS[bucket.dimension] ?? bucket.dimension}
                          value={formatPercent(share)}
                          sub={`${formatNumber(bucket.value)} conversations`}
                        />
                      );
                    })}
                  </div>
                </AdminPanel>
              )}

              {hasThinkWithMeMetrics && (
                <AdminPanel
                  title="Think With Me"
                  subtitle={`Recent reflective-question activity across the ${windowPhrase}, plus adoption snapshot.`}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <AdminMetricTile
                      label="Active Users"
                      value={formatNumber(twmWindowUsers)}
                      sub={windowThroughLabel}
                    />
                    <AdminMetricTile
                      label="Usage Share"
                      value={formatPercent(twmUsageShare)}
                      sub={`Activated users active in ${windowPhrase}`}
                    />
                    <AdminMetricTile
                      label="Activated Users"
                      value={formatNumber(twmActivatedUsers)}
                      sub={`${formatPercent(twmActivationRate)} of engaged users`}
                    />
                    <AdminMetricTile
                      label="Total Activations"
                      value={formatNumber(twmTotalActivations)}
                      sub={`Lifetime toggle-on events • ${formatDecimal(twmAvgActivations)} avg / user`}
                    />
                  </div>
                </AdminPanel>
              )}
            </div>
          )}
        </>
      )}
    </AdminPage>
  );
}

function filterMetricsToWindow(
  metrics: api.MetricPoint[],
  window: { start: Date; end: Date },
): api.MetricPoint[] {
  return metrics.filter((metric) => isWithin(parseDateOnly(metric.timestamp), window.start, window.end));
}

function ComparisonTile({ label, value, delta, trend, sub }: ComparisonCard) {
  const deltaColor =
    trend === 'up'
      ? 'text-[var(--status-success)]'
      : trend === 'down'
        ? 'text-[var(--status-error)]'
        : 'text-[var(--text-tertiary)]';

  return (
    <div className="rounded-[1.15rem] border border-[var(--border-default)] bg-white px-4 py-4 shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
      <p className="text-sm font-medium text-[var(--text-secondary)]">{label}</p>
      <div className="mt-2 flex items-end justify-between gap-3">
        <p className="text-3xl font-semibold tracking-tight text-[var(--text-primary)]">{value}</p>
        <span className={`text-sm font-semibold ${deltaColor}`}>{delta}</span>
      </div>
      <p className="mt-2 text-sm text-[var(--text-tertiary)]">{sub}</p>
    </div>
  );
}

function BarList({
  items,
  total,
  labelFormatter,
  valueFormatter,
}: {
  items: DimensionTotal[];
  total: number;
  labelFormatter: (label: string) => string;
  valueFormatter: (value: number, share: number) => string;
}) {
  return (
    <div className="space-y-3">
      {items.map((item) => {
        const share = total > 0 ? item.value / total : 0;
        return (
          <div
            key={item.dimension}
            className="grid grid-cols-[minmax(0,200px)_1fr_auto] items-center gap-4"
          >
            <span className="text-sm font-medium text-[var(--text-primary)]">
              {labelFormatter(item.dimension)}
            </span>
            <div className="h-3 rounded-full border border-[var(--border-subtle)] bg-[var(--surface-tertiary)] overflow-hidden">
              <div
                className="h-full rounded-full bg-[var(--accent-visual)]"
                style={{ width: `${Math.max(share * 100, item.value > 0 ? 3 : 0)}%` }}
              />
            </div>
            <span className="text-sm font-mono text-[var(--text-secondary)]">
              {valueFormatter(item.value, share)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function computeAvg(metrics: api.MetricPoint[], name: string): number {
  const values = metrics.filter((m) => m.metric_name === name && m.dimension === null);
  if (values.length === 0) return 0;
  return values.reduce((sum, metric) => sum + metric.value, 0) / values.length;
}

function getLatestMetricValue(metrics: api.MetricPoint[], name: string): number {
  const values = metrics
    .filter((m) => m.metric_name === name && m.dimension === null)
    .sort((a, b) => parseMetricTimestamp(b.timestamp) - parseMetricTimestamp(a.timestamp));
  return values[0]?.value ?? 0;
}

function getLatestDimensionSnapshot(
  metrics: api.MetricPoint[],
  name: string,
): DimensionTotal[] {
  const matching = metrics.filter((m) => m.metric_name === name && m.dimension);
  if (matching.length === 0) return [];
  const latestTimestamp = Math.max(...matching.map((m) => parseMetricTimestamp(m.timestamp)));
  return matching
    .filter((m) => parseMetricTimestamp(m.timestamp) === latestTimestamp && m.dimension)
    .map((m) => ({ dimension: m.dimension as string, value: m.value }))
    .sort((a, b) => b.value - a.value);
}

function sumDimensionMetrics(metrics: api.MetricPoint[], name: string): DimensionTotal[] {
  const totals = new Map<string, number>();
  for (const metric of metrics) {
    if (metric.metric_name !== name || !metric.dimension) continue;
    totals.set(metric.dimension, (totals.get(metric.dimension) ?? 0) + metric.value);
  }

  return Array.from(totals.entries())
    .map(([dimension, value]) => ({ dimension, value }))
    .sort((a, b) => b.value - a.value);
}

function orderByPreferredOrder(items: DimensionTotal[], preferredOrder: string[]): DimensionTotal[] {
  const preferredIndex = new Map(preferredOrder.map((value, index) => [value, index]));
  return [...items].sort((a, b) => {
    const aIndex = preferredIndex.get(a.dimension) ?? Number.MAX_SAFE_INTEGER;
    const bIndex = preferredIndex.get(b.dimension) ?? Number.MAX_SAFE_INTEGER;
    if (aIndex !== bIndex) return aIndex - bIndex;
    return b.value - a.value;
  });
}

function sortByValueWithPreferredOrder(
  items: DimensionTotal[],
  preferredOrder: string[],
): DimensionTotal[] {
  const preferredIndex = new Map(preferredOrder.map((value, index) => [value, index]));
  return [...items].sort((a, b) => {
    if (b.value !== a.value) return b.value - a.value;
    const aIndex = preferredIndex.get(a.dimension) ?? Number.MAX_SAFE_INTEGER;
    const bIndex = preferredIndex.get(b.dimension) ?? Number.MAX_SAFE_INTEGER;
    return aIndex - bIndex;
  });
}

function formatNumber(value: number): string {
  return value >= 1000 ? `${(value / 1000).toFixed(1)}k` : String(Math.round(value));
}

function formatDecimal(value: number): string {
  return value.toFixed(1);
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(0)}%`;
}

function formatShortDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

function formatSignedDelta(value: number, formatter: (n: number) => string): string {
  if (Math.abs(value) < 0.0001) return 'Flat';
  const sign = value > 0 ? '+' : '−';
  return `${sign}${formatter(Math.abs(value))}`;
}

function downloadMetricsCsv(workspaceName: string, metrics: api.MetricPoint[]) {
  const rows = [
    ['timestamp', 'metric_name', 'dimension', 'value'],
    ...metrics
      .slice()
      .sort((a, b) => {
        const byTime = parseMetricTimestamp(a.timestamp) - parseMetricTimestamp(b.timestamp);
        if (byTime !== 0) return byTime;
        const byMetric = a.metric_name.localeCompare(b.metric_name);
        if (byMetric !== 0) return byMetric;
        return (a.dimension ?? '').localeCompare(b.dimension ?? '');
      })
      .map((metric) => [
        metric.timestamp,
        metric.metric_name,
        metric.dimension ?? '',
        String(metric.value),
      ]),
  ];

  const csv = rows
    .map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${slugify(workspaceName)}-overview-metrics.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function buildWeeklyComparisonCards(
  metrics: api.MetricPoint[],
  opts: { experienceType: string; paidSubscribers: number },
): ComparisonCard[] {
  const availableDates = getAvailableDates(metrics);
  if (availableDates.length < 8) return [];

  const sorted = [...availableDates].sort((a, b) => a.getTime() - b.getTime());
  const latest = getCompletedWindowEndFromSortedDates(sorted);
  if (!latest) return [];

  const currentStart = shiftDays(latest, -6);
  const previousEnd = shiftDays(currentStart, -1);
  const previousStart = shiftDays(previousEnd, -6);

  if (sorted[0].getTime() > previousStart.getTime()) {
    return [];
  }

  if (opts.experienceType === 'ecommerce') {
    const currentConversations = sumMetricWindow(metrics, 'conversation.count', currentStart, latest);
    const previousConversations = sumMetricWindow(metrics, 'conversation.count', previousStart, previousEnd);
    const currentUsers = averageMetricWindow(metrics, 'conversation.active_users', currentStart, latest);
    const previousUsers = averageMetricWindow(metrics, 'conversation.active_users', previousStart, previousEnd);
    const currentAssist = sumMetricWindow(metrics, 'conversation.assisted_count', currentStart, latest);
    const previousAssist = sumMetricWindow(metrics, 'conversation.assisted_count', previousStart, previousEnd);
    const currentZeroRate = windowRatio(
      metrics,
      'diagnostic.rag_zero_result_count',
      'diagnostic.total_requests',
      currentStart,
      latest,
    );
    const previousZeroRate = windowRatio(
      metrics,
      'diagnostic.rag_zero_result_count',
      'diagnostic.total_requests',
      previousStart,
      previousEnd,
    );

    return [
      {
        label: 'Conversations',
        value: formatNumber(currentConversations),
        delta: formatSignedDelta(currentConversations - previousConversations, formatNumber),
        trend: compareTrend(currentConversations, previousConversations),
        sub: 'Last 7 complete days',
      },
      {
        label: 'Avg Daily Active Users',
        value: formatNumber(currentUsers),
        delta: formatSignedDelta(currentUsers - previousUsers, formatNumber),
        trend: compareTrend(currentUsers, previousUsers),
        sub: 'Complete week vs previous',
      },
      {
        label: 'Assisted Sessions',
        value: formatNumber(currentAssist),
        delta: formatSignedDelta(currentAssist - previousAssist, formatNumber),
        trend: compareTrend(currentAssist, previousAssist),
        sub: 'Link-assisted conversations',
      },
      {
        label: 'Zero-Result Rate',
        value: formatPercent(currentZeroRate),
        delta: formatSignedDelta(currentZeroRate - previousZeroRate, formatPercent),
        trend: compareTrend(previousZeroRate, currentZeroRate),
        sub: 'Knowledge gaps in complete week',
      },
    ];
  }

  const currentConversations = sumMetricWindow(metrics, 'conversation.count', currentStart, latest);
  const previousConversations = sumMetricWindow(metrics, 'conversation.count', previousStart, previousEnd);
  const currentUsers = averageMetricWindow(metrics, 'conversation.active_users', currentStart, latest);
  const previousUsers = averageMetricWindow(metrics, 'conversation.active_users', previousStart, previousEnd);
  const currentPaid = latestMetricInWindow(metrics, 'subscription.paid_active', currentStart, latest);
  const previousPaid = latestMetricInWindow(metrics, 'subscription.paid_active', previousStart, previousEnd);
  const currentAvgTurns = averageMetricWindow(metrics, 'conversation.avg_turns', currentStart, latest);
  const previousAvgTurns = averageMetricWindow(metrics, 'conversation.avg_turns', previousStart, previousEnd);

  return [
    {
      label: 'Conversations',
      value: formatNumber(currentConversations),
      delta: formatSignedDelta(currentConversations - previousConversations, formatNumber),
      trend: compareTrend(currentConversations, previousConversations),
      sub: 'Last 7 complete days',
    },
    {
      label: 'Avg Daily Active Users',
      value: formatNumber(currentUsers),
      delta: formatSignedDelta(currentUsers - previousUsers, formatNumber),
      trend: compareTrend(currentUsers, previousUsers),
      sub: 'Complete week vs previous',
    },
    {
      label: 'Paid Subscribers',
      value: formatNumber(currentPaid || opts.paidSubscribers),
      delta: formatSignedDelta((currentPaid || opts.paidSubscribers) - previousPaid, formatNumber),
      trend: compareTrend(currentPaid || opts.paidSubscribers, previousPaid),
      sub: 'Latest weekly snapshot',
    },
    {
      label: 'Avg Turns / Conversation',
      value: formatDecimal(currentAvgTurns),
      delta: formatSignedDelta(currentAvgTurns - previousAvgTurns, formatDecimal),
      trend: compareTrend(currentAvgTurns, previousAvgTurns),
      sub: 'Complete week vs previous',
    },
  ];
}

function getAvailableDates(metrics: api.MetricPoint[]): Date[] {
  const seen = new Map<string, Date>();
  for (const metric of metrics) {
    const parsed = parseDateOnly(metric.timestamp);
    if (parsed) {
      seen.set(parsed.toISOString(), parsed);
    }
  }
  return Array.from(seen.values());
}

function getCompletedWindow(
  metrics: api.MetricPoint[],
  windowDays: WindowPreset,
): { start: Date; end: Date } | null {
  const sorted = getAvailableDates(metrics).sort((a, b) => a.getTime() - b.getTime());
  const end = getCompletedWindowEndFromSortedDates(sorted);
  if (!end) return null;
  return {
    start: shiftDays(end, -(windowDays - 1)),
    end,
  };
}

function getCompletedWindowEndFromSortedDates(sortedDates: Date[]): Date | null {
  const latest = sortedDates.at(-1);
  if (!latest) return null;
  const yesterdayUtc = shiftDays(todayUtc(), -1);
  return latest.getTime() <= yesterdayUtc.getTime() ? latest : yesterdayUtc;
}

function parseDateOnly(value: string): Date | null {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate()));
}

function todayUtc(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
}

function shiftDays(date: Date, delta: number): Date {
  const next = new Date(date.getTime());
  next.setUTCDate(next.getUTCDate() + delta);
  return next;
}

function isWithin(date: Date | null, start: Date, end: Date): boolean {
  return date !== null && date.getTime() >= start.getTime() && date.getTime() <= end.getTime();
}

function sumMetricWindow(
  metrics: api.MetricPoint[],
  metricName: string,
  start: Date,
  end: Date,
): number {
  return metrics
    .filter((metric) => metric.metric_name === metricName && metric.dimension === null)
    .filter((metric) => isWithin(parseDateOnly(metric.timestamp), start, end))
    .reduce((sum, metric) => sum + metric.value, 0);
}

function averageMetricWindow(
  metrics: api.MetricPoint[],
  metricName: string,
  start: Date,
  end: Date,
): number {
  const points = metrics
    .filter((metric) => metric.metric_name === metricName && metric.dimension === null)
    .filter((metric) => isWithin(parseDateOnly(metric.timestamp), start, end));
  if (points.length === 0) return 0;
  return points.reduce((sum, metric) => sum + metric.value, 0) / points.length;
}

function latestMetricInWindow(
  metrics: api.MetricPoint[],
  metricName: string,
  start: Date,
  end: Date,
): number {
  const points = metrics
    .filter((metric) => metric.metric_name === metricName && metric.dimension === null)
    .filter((metric) => isWithin(parseDateOnly(metric.timestamp), start, end))
    .sort((a, b) => parseMetricTimestamp(b.timestamp) - parseMetricTimestamp(a.timestamp));
  return points[0]?.value ?? 0;
}

function windowRatio(
  metrics: api.MetricPoint[],
  numeratorName: string,
  denominatorName: string,
  start: Date,
  end: Date,
): number {
  const numerator = sumMetricWindow(metrics, numeratorName, start, end);
  const denominator = sumMetricWindow(metrics, denominatorName, start, end);
  return computeRatioFromTotals(numerator, denominator);
}

function computeRatioFromTotals(numerator: number, denominator: number): number {
  return denominator > 0 ? numerator / denominator : 0;
}

function compareTrend(current: number, previous: number): 'up' | 'down' | 'flat' {
  if (Math.abs(current - previous) < 0.0001) return 'flat';
  return current > previous ? 'up' : 'down';
}

function slugify(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function parseMetricTimestamp(value: string): number {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}
